import {useAppContext} from 'contexts/ApplicationContext';
import React, {useMemo} from 'react';
import {Stack} from 'react-bootstrap';
import {useSearchParams} from 'react-router-dom';
import {mobile} from 'shared/Breakpoints';
import {CBCButton} from 'shared/helpers';
import {parseHtmlString} from 'shared/helpers/HTMLParser';
import {useNativeRedirect} from 'shared/helpers/useNativeRedirect';
import Icon from 'shared/Icon';
import styled from 'styled-components';
import {useJobDialog} from 'components/customer/Job/helpers/useJobDialog';

export const JobConfirmation = () => {
    const {userProfile} = useAppContext();
    const [searchParams] = useSearchParams();
    const {redirect} = useNativeRedirect();
    const {openJobDialog, jobDialog} = useJobDialog();

    const text = useMemo(() => {
        const hasVariations = searchParams.get('variations') == '1';
        const hasFreight = searchParams.get('freight') == '1';

        if (hasVariations && hasFreight) {
            return 'Because your order includes variations and also needs to be freighted, our team will review the job to provide you with an accurate final price.';
        } else if (hasVariations && !hasFreight) {
            return 'Because your order includes variations, our team will review the job to provide you with an accurate final price.';
        } else if (!hasVariations && hasFreight) {
            return 'Because you&apos;ve requested for your order to be freighted, our team will review to provide an accurate freight price based on your location and order size.';
        }
    }, [searchParams]);

    return (
        <Container>
            <Icon iconName="Button-Tick-green.svg" />
            <h1>
                Success! Your job has been submitted and is with our team for
                review
            </h1>
            <Description>
                <strong>What happens next?</strong>
                {parseHtmlString(text)}
                <div>
                    <Icon iconName="email-primary.svg" /> Watch your inbox:
                    We&apos;ll email you once the review is complete.
                </div>

                <div>
                    <Icon iconName="Credit-Card.svg" /> You&apos;ll be able to
                    review the final quote and pay through your available
                    payment options.
                </div>

                <br />
                <strong>Got some questions?</strong>

                <div>
                    Get in touch with us at:
                    <ul>
                        {userProfile?.manufacturerPhone ? (
                            <li>
                                <strong>Contact Number:</strong>{' '}
                                <a
                                    href={`tel:${userProfile.manufacturerPhone}`}>
                                    {userProfile?.manufacturerPhone}
                                </a>
                            </li>
                        ) : null}
                        {userProfile?.manufacturerEmail ? (
                            <li>
                                <strong>Contact Email address:</strong>{' '}
                                <a
                                    href={`mailto:${userProfile?.manufacturerEmail}`}>
                                    {userProfile?.manufacturerEmail}
                                </a>
                            </li>
                        ) : null}
                    </ul>
                </div>
            </Description>
            <PrimaryText>Estimated Review Time: 1-2 business days</PrimaryText>
            <Stack
                gap={2}
                className="flex-column flex-md-row justify-content-center mt-2">
                <CBCButton
                    type="button"
                    iconName="Home-White.svg"
                    className="job-button button-blue full-width-xs"
                    onClick={redirect('/v2/')}>
                    Return home
                </CBCButton>

                <CBCButton
                    type="button"
                    iconName="Button-Add.svg"
                    className="job-button button-primary full-width-xs"
                    onClick={openJobDialog}>
                    Start new job
                </CBCButton>
                {jobDialog}
            </Stack>
        </Container>
    );
};

const PrimaryText = styled.div`
    color: ${({theme}) => theme.colors.primary.main};
    font-weight: bold;
`;

const Description = styled.div`
    text-align: left;
    font-size: 1em !important;
    line-height: 20px;

    > strong {
        font-size: 1.1em;
        display: block;
        margin-bottom: 15px;
    }

    > div {
        margin-top: 10px;
        font-size: 0.95em;

        > img {
            width: 25px;
            margin-right: 10px;
        }

        > ul {
            list-style: none;
            margin-left: 0;
            padding-left: 0;

            > li {
                margin-top: 5px;
            }
        }
    }
`;

export const Container = styled.div`
    border-radius: 15px;
    box-shadow: 0 0 4px 2px #e5e5e5;
    padding: 20px 35px 35px;
    max-width: 800px;
    margin: 50px auto;
    text-align: center;

    > img {
        width: 100px;
    }

    > h1 {
        font-size: 2.3em;
        background: none;
        display: block;
        font-weight: 400;
        color: ${({theme}) => theme.colors.primary.main};
    }

    > a {
        border-radius: 15px;
        color: white;
        padding: 14px 30px;
        background: ${({theme}) => theme.colors.primary.main};
        display: block;
        max-width: 40%;
        margin: 15px auto;
        text-decoration: none;
        font-size: 1.2em;
        font-weight: 400;
    }

    > div {
        font-size: 1.2em;
    }

    ${mobile`
        > a {
            max-width: 80%;
        }    
    `}
`;
